import React, { useCallback, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Link2, Plug, X, Unplug, RefreshCw, CheckCircle2, AlertCircle, Search, Settings2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { useCrmConnection, CrmConnectionStatus } from '../../hooks/useCrmConnection';
import {
  useZohoIntegration,
  type ZohoSetupData,
} from '../../hooks/useZohoIntegration';
import { FeatureGate } from '../../components/FeatureGate';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

interface ConnectModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (args: {
    clientId: string;
    clientSecret: string;
    authUrl: string;
    adviserContactIds: string[];
    scope?: string;
  }) => Promise<void>;
  submitting: boolean;
  error: string | null;
  companyId: string | undefined;
}

const ConnectMyCrmModal: React.FC<ConnectModalProps> = ({ open, onClose, onSubmit, submitting, error, companyId }) => {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [adviserIdsRaw, setAdviserIdsRaw] = useState('');
  const [scope, setScope] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [discoveredScopes, setDiscoveredScopes] = useState<string[] | null>(null);
  const [selectedDiscovered, setSelectedDiscovered] = useState<Set<string>>(new Set());

  const handleDiscover = useCallback(async () => {
    if (!authUrl.trim() || !companyId) return;
    setDiscovering(true);
    setDiscoverError(null);
    setDiscoveredScopes(null);
    try {
      const fn = httpsCallable<
        { companyId: string; authUrl: string },
        { scopesSupported: string[] }
      >(functions, 'discoverMyCrmScopes');
      const { data } = await fn({ companyId, authUrl: authUrl.trim() });
      setDiscoveredScopes(data.scopesSupported);
    } catch (err) {
      setDiscoverError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiscovering(false);
    }
  }, [authUrl, companyId]);

  // Keep `scope` field in sync with the chip selection so admin sees the
  // space-separated string that will actually be sent.
  const toggleDiscovered = useCallback((s: string) => {
    setSelectedDiscovered((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      // Reflect selection into the scope input.
      setScope(Array.from(next).join(' '));
      return next;
    });
  }, []);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const adviserContactIds = adviserIdsRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    await onSubmit({
      clientId: clientId.trim(),
      clientSecret: clientSecret.trim(),
      authUrl: authUrl.trim(),
      adviserContactIds,
      ...(scope.trim() ? { scope: scope.trim() } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-xl rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">Connect MyCRM</h3>
            <p className="text-sm text-gray-400 mt-1">
              Credentials are encrypted at rest in Google Secret Manager — they are never written to
              Firestore and never visible to Valutten staff.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              MyCRM Client ID
            </label>
            <input
              type="text"
              required
              autoComplete="off"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              placeholder="0oazrldwbzff2bVmr357"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              MyCRM Client Secret
            </label>
            <input
              type="password"
              required
              autoComplete="off"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
              placeholder="Provided by LMG API support (LMG labels this 'password')"
            />
            <p className="text-xs text-gray-500 mt-1">
              LMG shares this as "password" — in OAuth terms it's your client_secret. We validate it
              against MyCRM before storing.
            </p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              MyCRM Auth URL
            </label>
            <input
              type="url"
              required
              autoComplete="off"
              value={authUrl}
              onChange={(e) => setAuthUrl(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500 font-mono text-sm"
              placeholder="https://auth.mycrm.finance/oauth2/aus.../v1/token"
            />
            <p className="text-xs text-gray-500 mt-1">
              The OAuth token endpoint LMG issued for your tenant (labeled "AUTH Url" in your
              credential bundle). Must be HTTPS. This is per-customer — no default.
            </p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1">
              Adviser Contact IDs (required, comma or space separated)
            </label>
            <textarea
              value={adviserIdsRaw}
              onChange={(e) => setAdviserIdsRaw(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500 font-mono text-sm"
              placeholder="12345, 67890, 11122"
            />
            <p className="text-xs text-gray-500 mt-1">
              Each adviser the sync should cover. MyCRM requires a per-request <code>UserId</code>{' '}
              header; the sync iterates each ID individually.
            </p>
          </div>

          <div>
            <div className="flex items-end justify-between mb-1">
              <label className="block text-xs uppercase tracking-wider text-gray-400">
                OAuth Scopes (optional)
              </label>
              <button
                type="button"
                onClick={handleDiscover}
                disabled={!authUrl.trim() || discovering}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                title="Fetch the scopes your tenant's Okta auth server has configured"
              >
                {discovering ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Search className="w-3 h-3" />
                )}
                {discovering ? 'Discovering…' : 'Discover Scopes'}
              </button>
            </div>
            <input
              type="text"
              autoComplete="off"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500 font-mono text-sm"
              placeholder="api"
            />
            {discoveredScopes && discoveredScopes.length > 0 && (
              <div className="mt-2 p-2 rounded-md border border-gray-700 bg-gray-800/50">
                <p className="text-xs text-gray-400 mb-1.5">
                  Your tenant supports {discoveredScopes.length} scope{discoveredScopes.length === 1 ? '' : 's'}. Click to add/remove:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {discoveredScopes.map((s) => {
                    const active = selectedDiscovered.has(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleDiscovered(s)}
                        className={`px-2 py-0.5 rounded-full text-xs font-mono border transition-colors ${
                          active
                            ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                            : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {discoveredScopes && discoveredScopes.length === 0 && (
              <p className="text-xs text-amber-400 mt-2">
                Metadata endpoint returned no scopes. Your tenant likely only exposes a single scope — try leaving this field blank and see if Connect succeeds.
              </p>
            )}
            {discoverError && (
              <p className="text-xs text-red-400 mt-2">Discover failed: {discoverError}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to use <code>api</code> (default; LMG's root scope, authorized on every MyCRM tenant).
              Narrower scopes like <code>api.read</code> are opt-in per tenant and must be provisioned by LMG first.
              Enter the Auth URL above then click <strong>Discover Scopes</strong> to list the exact scopes your tenant has.
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                submitting ||
                !clientId ||
                !clientSecret ||
                !authUrl ||
                !adviserIdsRaw.trim()
              }
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
              {submitting ? 'Validating…' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ZohoMappingsModalProps {
  setup: ZohoSetupData | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (mappings: Array<{
    brokerDocId: string;
    zohoOwnerId: string;
    zohoAssigneeId: string | null;
  }>) => Promise<void>;
}

const ZohoMappingsModal: React.FC<ZohoMappingsModalProps> = ({
  setup,
  saving,
  error,
  onClose,
  onSave,
}) => {
  const initial = useMemo(() => {
    const byBroker = new Map(setup?.mappings.map((mapping) => [mapping.brokerDocId, mapping]));
    return Object.fromEntries((setup?.brokers ?? []).map((broker) => {
      const mapping = byBroker.get(broker.brokerDocId);
      return [broker.brokerDocId, {
        zohoOwnerId: mapping?.zohoOwnerId ?? '',
        zohoAssigneeId: mapping?.zohoAssigneeId ?? '',
      }];
    }));
  }, [setup]);
  const [draft, setDraft] = useState(initial);

  React.useEffect(() => setDraft(initial), [initial]);
  if (!setup) return null;

  const save = async () => {
    await onSave(
      setup.brokers
        .filter((broker) => draft[broker.brokerDocId]?.zohoOwnerId)
        .map((broker) => ({
          brokerDocId: broker.brokerDocId,
          zohoOwnerId: draft[broker.brokerDocId].zohoOwnerId,
          zohoAssigneeId: draft[broker.brokerDocId].zohoAssigneeId || null,
        })),
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-white">Zoho broker mappings</h3>
            <p className="text-sm text-gray-400 mt-1">
              Choose the Zoho record owner for each broker and an optional task assignee.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-3">
          {setup.brokers.map((broker) => (
            <div key={broker.brokerDocId} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center rounded-md border border-gray-700 p-3">
              <div className="text-sm font-medium text-white">{broker.name}</div>
              <select
                value={draft[broker.brokerDocId]?.zohoOwnerId ?? ''}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  [broker.brokerDocId]: {
                    ...current[broker.brokerDocId],
                    zohoOwnerId: event.target.value,
                  },
                }))}
                className="rounded-md bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm"
              >
                <option value="">No owner mapping</option>
                {setup.users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
              <select
                value={draft[broker.brokerDocId]?.zohoAssigneeId ?? ''}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  [broker.brokerDocId]: {
                    ...current[broker.brokerDocId],
                    zohoAssigneeId: event.target.value,
                  },
                }))}
                className="rounded-md bg-gray-800 border border-gray-700 text-white px-3 py-2 text-sm"
              >
                <option value="">Task owner uses record owner</option>
                {setup.users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-gray-700 text-white">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save mappings'}
          </button>
        </div>
      </div>
    </div>
  );
};

function statusBadge(conn: CrmConnectionStatus): { label: string; className: string; Icon: React.ComponentType<{ className?: string }> } {
  if (conn.status === 'active' && conn.lastSyncResult === 'success') {
    return { label: 'Healthy', className: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', Icon: CheckCircle2 };
  }
  if (conn.status === 'active') {
    return { label: 'Pending first sync', className: 'text-blue-400 bg-blue-500/10 border-blue-500/30', Icon: Plug };
  }
  if (conn.status === 'revoked') {
    return { label: 'Revoked', className: 'text-gray-400 bg-gray-500/10 border-gray-500/30', Icon: Unplug };
  }
  return { label: conn.status, className: 'text-red-400 bg-red-500/10 border-red-500/30', Icon: AlertCircle };
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return '—';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const IntegrationsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx.userData?.companyId;
  const isSuperAdmin = ctx.userData?.role === 'super_admin';

  const { connections, loading, error, refresh, connect, disconnect, triggerSync } =
    useCrmConnection(companyId);
  const zoho = useZohoIntegration(companyId);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ connectionId: string; action: 'sync' | 'disconnect' } | null>(null);
  const [pendingZohoAction, setPendingZohoAction] = useState<string | null>(null);
  const [zohoMessage, setZohoMessage] = useState<string | null>(null);
  const [zohoError, setZohoError] = useState<string | null>(null);
  const [zohoSetup, setZohoSetup] = useState<ZohoSetupData | null>(null);

  const mycrmConnection = useMemo(
    () => connections.find((c) => c.provider === 'mycrm' && c.status !== 'revoked'),
    [connections]
  );

  const handleConnect = useCallback(
    async (args: {
      clientId: string;
      clientSecret: string;
      authUrl: string;
      adviserContactIds: string[];
      scope?: string;
    }) => {
      setModalSubmitting(true);
      setModalError(null);
      try {
        await connect(args);
        setModalOpen(false);
      } catch (err) {
        setModalError(err instanceof Error ? err.message : String(err));
      } finally {
        setModalSubmitting(false);
      }
    },
    [connect]
  );

  const handleSync = useCallback(
    async (connectionId: string) => {
      setPendingAction({ connectionId, action: 'sync' });
      try {
        await triggerSync(connectionId);
      } finally {
        setPendingAction(null);
      }
    },
    [triggerSync]
  );

  const handleDisconnect = useCallback(
    async (connectionId: string) => {
      if (!window.confirm('Disconnect MyCRM? The stored credentials will be permanently deleted from Secret Manager.')) {
        return;
      }
      setPendingAction({ connectionId, action: 'disconnect' });
      try {
        await disconnect(connectionId);
      } finally {
        setPendingAction(null);
      }
    },
    [disconnect]
  );

  const runZohoAction = useCallback(async (action: string, work: () => Promise<void>) => {
    setPendingZohoAction(action);
    setZohoError(null);
    setZohoMessage(null);
    try {
      await work();
    } catch (cause) {
      setZohoError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setPendingZohoAction(null);
    }
  }, []);

  const handleZohoConfigure = useCallback(async () => {
    await runZohoAction('configure', async () => {
      setZohoSetup(await zoho.getSetupData());
    });
  }, [runZohoAction, zoho]);

  const handleZohoSaveMappings = useCallback(async (mappings: Array<{
    brokerDocId: string;
    zohoOwnerId: string;
    zohoAssigneeId: string | null;
  }>) => {
    await runZohoAction('save-mappings', async () => {
      await zoho.saveMappings(mappings);
      setZohoSetup(null);
      setZohoMessage(`Saved ${mappings.length} broker mapping${mappings.length === 1 ? '' : 's'}.`);
    });
  }, [runZohoAction, zoho]);

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        icon={Link2}
        title="Integrations"
        subtitle="Connect your CRM so Valutten can cross-reference settled deals against your trail commission data"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {!isSuperAdmin && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          Only super_admin users can add or remove integrations. You can view connection status.
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Plug className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">MyCRM</h3>
              <p className="text-sm text-gray-400 mt-0.5">
                LMG Broker Engine. Required for trail leakage detection.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mycrmConnection ? (
              (() => {
                const b = statusBadge(mycrmConnection);
                return (
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${b.className}`}
                  >
                    <b.Icon className="w-3.5 h-3.5" />
                    {b.label}
                  </span>
                );
              })()
            ) : (
              <span className="text-xs text-gray-500">Not connected</span>
            )}
          </div>
        </div>

        {mycrmConnection ? (
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Last sync</div>
              <div className="text-sm text-white mt-1">{formatRelative(mycrmConnection.lastSyncAt)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Deals synced</div>
              <div className="text-sm text-white mt-1">{mycrmConnection.dealsSynced.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Advisers</div>
              <div className="text-sm text-white mt-1">
                {mycrmConnection.advisersSynced.toLocaleString()} /{' '}
                {mycrmConnection.adviserContactIds.length} scoped
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Last result</div>
              <div className="text-sm text-white mt-1">{mycrmConnection.lastSyncResult ?? '—'}</div>
            </div>
          </div>
        ) : null}

        {isSuperAdmin && (
          <div className="mt-5 flex items-center gap-2">
            <FeatureGate
              feature="broker_activity"
              fallback={(
                <p className="text-sm text-gray-400">
                  MyCRM connection management is not included in this subscription.
                </p>
              )}
            >
              {!mycrmConnection ? (
                <button
                  onClick={() => {
                    setModalError(null);
                    setModalOpen(true);
                  }}
                  className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Plug className="w-4 h-4" />
                  Connect MyCRM
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handleSync(mycrmConnection.connectionId)}
                    disabled={pendingAction?.connectionId === mycrmConnection.connectionId}
                    className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${
                        pendingAction?.action === 'sync' &&
                        pendingAction?.connectionId === mycrmConnection.connectionId
                          ? 'animate-spin'
                          : ''
                      }`}
                    />
                    Sync now
                  </button>
                  <Link
                    to="/settings/crm-mappings"
                    className="px-4 py-2 rounded-md border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 inline-flex items-center gap-2"
                  >
                    <Settings2 className="w-4 h-4" />
                    Mappings
                  </Link>
                </>
              )}
            </FeatureGate>
            {mycrmConnection && (
                <button
                  onClick={() => handleDisconnect(mycrmConnection.connectionId)}
                  disabled={pendingAction?.connectionId === mycrmConnection.connectionId}
                  className="px-4 py-2 rounded-md border border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Unplug className="w-4 h-4" />
                  Disconnect
                </button>
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Zoho CRM</h3>
              <p className="text-sm text-gray-400 mt-0.5">
                Retention tasks, expected fees and reconciliation for the MyMF workflow.
              </p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${
            zoho.connection?.status === 'active'
              ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
              : 'text-gray-400 bg-gray-500/10 border-gray-500/30'
          }`}>
            {zoho.connection?.status === 'active' ? 'Connected' : 'Not connected'}
          </span>
        </div>

        {zoho.connection?.status === 'active' && (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Connected by</div>
              <div className="text-white mt-1">{zoho.connection.connectedByEmail ?? 'Unknown'}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Active Zoho users</div>
              <div className="text-white mt-1">{zoho.connection.activeUserCount.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">Data centre</div>
              <div className="text-white mt-1">{zoho.connection.location ?? 'Detected by Zoho'}</div>
            </div>
          </div>
        )}

        {zoho.connection?.reconnectCleanupRequired && (
          <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            The previous Zoho token still needs revocation. Contact VALUTTEN before continuing.
          </div>
        )}
        {zoho.connection?.secretCleanupRequired && (
          <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            Zoho access is revoked, but its encrypted secret resource still needs cleanup.
            Contact VALUTTEN.
          </div>
        )}
        {(zoho.error || zohoError) && (
          <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {zoho.error ?? zohoError}
          </div>
        )}
        {zohoMessage && <p className="mt-4 text-sm text-emerald-300">{zohoMessage}</p>}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <FeatureGate
            feature="crm_integration"
            anyOf="broker_activity"
            showLoading
            fallback={(
              <p className="text-sm text-gray-400">
                Zoho connection management is not included in this subscription.
              </p>
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => runZohoAction('connect', zoho.prepareOAuth)}
                disabled={
                  Boolean(pendingZohoAction) ||
                  zoho.connection?.reconnectCleanupRequired === true
                }
                className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              >
                {zoho.connection?.status === 'active' ? 'Reconnect Zoho' : 'Connect Zoho'}
              </button>
              {zoho.connection?.status === 'active' && (
                <>
                  <button
                    onClick={() => runZohoAction('test', async () => {
                      const result = await zoho.test();
                      setZohoMessage(`Connection healthy. ${result.activeUserCount} active users and ${result.taskFieldCount} Task fields are available.`);
                    })}
                    disabled={Boolean(pendingZohoAction)}
                    className="px-4 py-2 rounded-md border border-gray-600 text-gray-200 hover:border-gray-400 disabled:opacity-50"
                  >
                    Test connection
                  </button>
                  <button
                    onClick={handleZohoConfigure}
                    disabled={Boolean(pendingZohoAction)}
                    className="px-4 py-2 rounded-md border border-gray-600 text-gray-200 hover:border-gray-400 disabled:opacity-50"
                  >
                    Broker mappings
                  </button>
                </>
              )}
            </div>
          </FeatureGate>

          {zoho.connection?.status === 'active' && (
            <button
              onClick={() => {
                if (!window.confirm('Disconnect Zoho and revoke its refresh token?')) return;
                void runZohoAction('disconnect', zoho.disconnect);
              }}
              disabled={Boolean(pendingZohoAction)}
              className="px-4 py-2 rounded-md border border-red-500/40 text-red-300 hover:border-red-400 disabled:opacity-50"
            >
              Disconnect Zoho
            </button>
          )}
        </div>
      </div>

      {isSuperAdmin && (
        <ConnectMyCrmModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleConnect}
          submitting={modalSubmitting}
          error={modalError}
          companyId={companyId}
        />
      )}
      <ZohoMappingsModal
        setup={zohoSetup}
        saving={pendingZohoAction === 'save-mappings'}
        error={zohoError}
        onClose={() => setZohoSetup(null)}
        onSave={handleZohoSaveMappings}
      />
    </div>
  );
};

export default IntegrationsPage;
